/**
 * Pure aggregation functions for the FCCERS dashboard.
 * All take a filtered `DashboardObservation[]` and return derived data
 * ready for chart components.
 */

import type { DashboardObservation, SubscaleAverages } from './types';
import { SUBSCALE_META } from './types';

// ─── Helpers ────────────────────────────────────────────────────────────

function mean(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function round2(v: number | null): number | null {
  return v === null ? null : Math.round(v * 100) / 100;
}

// ─── KPIs ───────────────────────────────────────────────────────────────

export interface KpiData {
  totalObservations: number;
  systemAverage: number | null;
  childrenServed: number;
  sitesObserved: number;
}

export function computeKpis(obs: DashboardObservation[]): KpiData {
  const avgs = obs.map((o) => o.totalAverage).filter((v): v is number => v !== null);
  return {
    totalObservations: obs.length,
    systemAverage: round2(mean(avgs)),
    childrenServed: obs.reduce((sum, o) => sum + o.childrenPresent, 0),
    sitesObserved: new Set(obs.map((o) => o.siteName)).size,
  };
}

// ─── Subscale radar ─────────────────────────────────────────────────────

export interface RadarPoint {
  subscale: string;
  fullName: string;
  average: number;
  color: string;
}

export function computeRadarData(obs: DashboardObservation[]): RadarPoint[] {
  return SUBSCALE_META.map((s) => {
    const vals = obs
      .map((o) => o.subscaleAverages[s.id])
      .filter((v): v is number => v !== null);
    return {
      subscale: s.shortName,
      fullName: s.name,
      average: round2(mean(vals)) ?? 0,
      color: s.color,
    };
  });
}

// ─── Site ranking ───────────────────────────────────────────────────────

export interface SiteRanking {
  siteName: string;
  observationCount: number;
  average: number | null;
  subscaleAverages: SubscaleAverages;
}

export function computeSiteRankings(obs: DashboardObservation[]): SiteRanking[] {
  const grouped = new Map<string, DashboardObservation[]>();
  for (const o of obs) {
    const list = grouped.get(o.siteName) ?? [];
    list.push(o);
    grouped.set(o.siteName, list);
  }

  return Array.from(grouped.entries())
    .map(([siteName, siteObs]) => {
      const avgs = siteObs.map((o) => o.totalAverage).filter((v): v is number => v !== null);
      const subAvg = (key: keyof SubscaleAverages) => {
        const vals = siteObs.map((o) => o.subscaleAverages[key]).filter((v): v is number => v !== null);
        return round2(mean(vals));
      };
      return {
        siteName,
        observationCount: siteObs.length,
        average: round2(mean(avgs)),
        subscaleAverages: {
          S1: subAvg('S1'),
          S2: subAvg('S2'),
          S3: subAvg('S3'),
          S4: subAvg('S4'),
          S5: subAvg('S5'),
          S6: subAvg('S6'),
        },
      };
    })
    .sort((a, b) => (b.average ?? 0) - (a.average ?? 0));
}

// ─── Grouped bar data (site comparison) ─────────────────────────────────

export interface SiteBarDatum {
  siteName: string;
  S1: number;
  S2: number;
  S3: number;
  S4: number;
  S5: number;
  S6: number;
}

export function computeSiteBarData(obs: DashboardObservation[]): SiteBarDatum[] {
  const rankings = computeSiteRankings(obs);
  return rankings.map((r) => ({
    siteName: r.siteName,
    S1: r.subscaleAverages.S1 ?? 0,
    S2: r.subscaleAverages.S2 ?? 0,
    S3: r.subscaleAverages.S3 ?? 0,
    S4: r.subscaleAverages.S4 ?? 0,
    S5: r.subscaleAverages.S5 ?? 0,
    S6: r.subscaleAverages.S6 ?? 0,
  }));
}

// ─── Trend line data ────────────────────────────────────────────────────

export interface TrendPoint {
  date: string;
  siteName: string;
  totalAverage: number;
}

export function computeTrendData(obs: DashboardObservation[]): TrendPoint[] {
  return obs
    .filter((o) => o.totalAverage !== null && o.observationDate)
    .map((o) => ({
      date: o.observationDate,
      siteName: o.siteName,
      totalAverage: o.totalAverage!,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Monthly system-wide subscale averages for trend charts. */
export interface MonthlySubscalePoint {
  month: string;
  S1: number | null;
  S2: number | null;
  S3: number | null;
  S4: number | null;
  S5: number | null;
  S6: number | null;
}

export function computeMonthlySubscaleTrends(obs: DashboardObservation[]): MonthlySubscalePoint[] {
  const byMonth = new Map<string, DashboardObservation[]>();
  for (const o of obs) {
    const month = o.observationDate.slice(0, 7); // yyyy-MM
    const list = byMonth.get(month) ?? [];
    list.push(o);
    byMonth.set(month, list);
  }

  return Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, monthObs]) => {
      const sub = (key: keyof SubscaleAverages) => {
        const vals = monthObs.map((o) => o.subscaleAverages[key]).filter((v): v is number => v !== null);
        return round2(mean(vals));
      };
      return { month, S1: sub('S1'), S2: sub('S2'), S3: sub('S3'), S4: sub('S4'), S5: sub('S5'), S6: sub('S6') };
    });
}

/** Unique sites from dataset (for filter dropdowns). */
export function uniqueSites(obs: DashboardObservation[]): string[] {
  return [...new Set(obs.map((o) => o.siteName))].sort();
}

/** Unique observers from dataset (for filter dropdowns). */
export function uniqueObservers(obs: DashboardObservation[]): string[] {
  return [...new Set(obs.map((o) => o.observerName))].sort();
}
